import React from 'react';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import { ChevronRight, MapPin } from 'lucide-react';
import { Card, CardContent, Badge } from '../ui';
import EntityLogo from '../ui/EntityLogo';
import { ETAPA_LABELS, ETAPA_STYLES } from './torneoStatus';
import { parseLocalDate } from '../../lib/dateUtils';
import { cn, fixMojibake } from '../../lib/utils';

// La tarjeta de un torneo, la misma para los dos mundos.
//
// Antes cada lista tenia la suya y hablaban idiomas distintos: una mostraba etapas
// (Borrador, En curso) y la otra decia "Con licencia" -- que no significa nada para un
// coach. Mismo objeto, mismo lenguaje; lo unico que cambia es el verbo y las acciones.
//
// Y contesta la pregunta con la que se abre la pantalla. Antes decia "3 inscritos, 12
// jugados": nadie llega ahi preguntandose cuantos inscritos hay. Llegan preguntando
// CUANDO JUEGAN.

const fechaCorta = (f) => {
  if (!f) return '';
  try {
    return format(parseLocalDate(f) || new Date(), "EEE d MMM", { locale: es });
  } catch { return f; }
};

// Cuantos grupos van, y cuantos siguen esperando respuesta (solo en la red). El record
// de cada grupo ya no va aqui: con 20 torneos era una tarjeta llena de "sin jugar" y
// "2-0-1" que nadie lee desde la lista (el dueno, 2026-09-09). Vive en la pestana
// Record del torneo, que es donde se pregunta.
const lineaDeGrupos = (inscripciones) => {
  const total = inscripciones.length;
  if (total === 0) return 'Sin grupos inscritos';
  const esperando = inscripciones.filter((i) => i.estatus === 'solicitada').length;
  const base = `${total} ${total === 1 ? 'grupo inscrito' : 'grupos inscritos'}`;
  return esperando > 0 ? `${base} · ${esperando} esperando respuesta` : base;
};

const TorneoCard = ({ torneo, modo = 'participas', proximo = null, inscripciones = [], onOpen, acciones = null }) => {
  const etapa = torneo.etapa || 'borrador';
  const fuente = torneo.liga_nombre || torneo.organizadora_nombre || torneo.ligas?.nombre || null;
  const fuenteLogo = torneo.liga_logo || torneo.ligas?.logo_url || null;

  return (
    <Card className="overflow-hidden transition-shadow hover:shadow-md">
      <CardContent className="space-y-3 p-4">
        {/* Identidad */}
        <button
          type="button"
          onClick={onOpen}
          className="flex w-full items-start gap-3 text-left"
        >
          <EntityLogo src={torneo.logo_url} name={torneo.nombre} className="h-10 w-10 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <h3 className="truncate text-base font-semibold leading-tight">
                {fixMojibake(torneo.nombre || 'Torneo')}
              </h3>
              <Badge variant="secondary" className="shrink-0 text-[10px] font-normal">
                {modo === 'organizas' ? 'Organizas' : 'Participas'}
              </Badge>
              <Badge variant="outline" className={cn('shrink-0 text-[10px] font-normal', ETAPA_STYLES[etapa])}>
                {ETAPA_LABELS[etapa] || etapa}
              </Badge>
            </div>
            {fuente && (
              <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                {fuenteLogo && <EntityLogo src={fuenteLogo} name={fuente} className="h-4 w-4 shrink-0" />}
                <span className="truncate">{fixMojibake(fuente)}</span>
              </div>
            )}
          </div>
          <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
        </button>

        {/* Lo que se vino a preguntar */}
        {proximo && (
          <div className="rounded-lg bg-muted/50 px-3 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Próximo</p>
            <p className="mt-0.5 truncate text-sm font-medium">
              {fixMojibake(proximo.grupo_nombre || 'Tu equipo')}
              <span className="text-muted-foreground"> vs </span>
              {fixMojibake(proximo.rival || 'Por definir')}
            </p>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{fechaCorta(proximo.fecha)}</span>
              {proximo.hora && <span>{String(proximo.hora).slice(0, 5)}</span>}
              {proximo.cancha && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" />{fixMojibake(proximo.cancha)}
                </span>
              )}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{lineaDeGrupos(inscripciones)}</p>
          {acciones}
        </div>
      </CardContent>
    </Card>
  );
};

export default TorneoCard;
